import { FirebaseOptions, initializeApp, FirebaseError } from 'firebase/app';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
    getLimitedUseToken,
    getToken,
    initializeAppCheck,
    ReCaptchaEnterpriseProvider
} from 'firebase/app-check';
import {
    ENABLE_APP_CHECK,
    FIREBASE_CONFIG,
    RECAPTCHA_SITE_KEY
} from './config';

const firebaseConfig: FirebaseOptions = FIREBASE_CONFIG
    ? (JSON.parse(FIREBASE_CONFIG) as FirebaseOptions)
    : {};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: false
});

export function getAppCheckTokenHeaders(): Promise<Record<string, string>> {
    if (ENABLE_APP_CHECK) {
        return getToken(appCheck, /* forceRefresh= */ false)
            .then(({ token }) => ({
                'x-firebase-appcheck': token
            }))
            .catch((error) => {
                console.error('Error getting App Check token', error);
                return {};
            });
    } else {
        return Promise.resolve({});
    }
}

/**
 * App Check headers for an endpoint with replay protection. Mints a fresh
 * single-use token per call — a cached token from `getAppCheckTokenHeaders`
 * would be rejected as a replay on its second use.
 */
export function getAppCheckLimitedUseTokenHeaders(): Promise<
    Record<string, string>
> {
    if (!ENABLE_APP_CHECK) {
        return Promise.resolve({});
    }

    return getLimitedUseToken(appCheck).then(({ token }) => ({
        'x-firebase-appcheck': token
    }));
}

export function getAppCheckToken(): Promise<string> {
    return getToken(appCheck, /* forceRefresh= */ false).then(
        ({ token }) => token
    );
}

export function isFirebaseError(error: unknown): boolean {
    return error instanceof FirebaseError;
}

export function logAnalyticsEvent(
    eventName: string,
    eventParams?: {
        [key: string]: unknown;
    }
): void {
    logEvent(analytics, eventName, eventParams);
}
